import { getDefaultCPTCodes } from "constants/cptCodes";
import queryMetaData from "services/queryMetaData";
import querySecureData from "services/querySecureData";
import { orchid_decode_key } from "utils/OrchidEncryption";
import decryptWhenHasData from "utils/decryptWhenHasData";
import findSignedAddendumNoteCodes from "../scenes/note-fill-in/utils/findSignedAddendumNoteCodes";
import findUnsignedAddendum from "../scenes/note-fill-in/utils/findUnsignedAddendum";

const getFullNoteDataByNoteId = async (
  note_data_id: string | number,
  on_behalf_of?: string,
) => {
  const metaData = await queryMetaData({
    type: "note",
    id: note_data_id,
    ...(on_behalf_of && { onBehalfOf: on_behalf_of }),
  });

  if (!metaData?.cipher_key) {
    return {
      metaData,
    };
  }

  const decodeKey = await orchid_decode_key(metaData?.cipher_key);

  let secureData: Partial<NoteData> = {};
  try {
    secureData = await querySecureData({
      type: "note",
      id: note_data_id,
      ...(on_behalf_of && { onBehalfOf: on_behalf_of }),
    });
  } catch (error) {
    /**
     * swallow the error
     * @see https://orchidhealth.atlassian.net/browse/OIM-765
     */
  }

  // note main body
  const data = (await decryptWhenHasData(decodeKey, secureData?.data)) ?? {};

  // cpt and icd codes
  const noteCodes =
    (await decryptWhenHasData(
      decodeKey,
      secureData?.client_service_and_diagnosis,
    )) || {};

  // when there is no secure data meaning this is a new note
  if (!secureData?.data && !secureData?.client_service_and_diagnosis) {
    if (metaData?.appointment) {
      noteCodes.service_codes = getDefaultCPTCodes(
        new Date(metaData.appointment.appointment_end).valueOf() -
          new Date(metaData.appointment.appointment_start).valueOf(),
        false,
      );
    } else if (metaData?.group_session) {
      noteCodes.service_codes = getDefaultCPTCodes(
        new Date(metaData.group_session.group_session_end).valueOf() -
          new Date(metaData.group_session.group_session_start).valueOf(),
        true,
      );
    }
  }

  // comments
  const comment =
    (await decryptWhenHasData(decodeKey, secureData?.note_comment)) || "";

  // addendums
  const addendums = await Promise.all(
    (secureData?.addendum || []).map(async (el) => ({
      ...el,
      addendum: await decryptWhenHasData(decodeKey, el.addendum),
      client_service_and_diagnosis: await decryptWhenHasData(
        decodeKey,
        el.client_service_and_diagnosis,
      ),
    })),
  );

  const signedAddendums = addendums.filter((el) => el.sign_timestamp);

  // check if there is any unsigned addendum
  const unsignedAddendum = findUnsignedAddendum(addendums);

  // if there is unsignedaddendum, should set default addendum agreement to true
  const addendumAgreementAgreed = !!unsignedAddendum;

  // addendums might have client_service_and_diagnosis
  const signedAddendumNoteCodes =
    findSignedAddendumNoteCodes(addendums)?.client_service_and_diagnosis;

  return {
    metaData,
    decodeKey,
    secureData,
    data,
    noteCodes,
    comment,
    signedAddendums,
    unsignedAddendum,
    addendumAgreementAgreed,
    signedAddendumNoteCodes,
  };
};

export const fullNoteDataQueryOptions = (noteDataId: number) => ({
  queryKey: ["fullNoteData", noteDataId],
  queryFn: () => getFullNoteDataByNoteId(noteDataId),
});

export default getFullNoteDataByNoteId;
